// src/App.js  — FIXED: AuthProvider wraps Router so ProtectedRoute works
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';

// Admin Components
import AdminLogin        from './components/admin/AdminLogin';
import Dashboard         from './components/admin/Dashboard';
import ProductManagement from './components/admin/ProductManagement';
import FridgeManagement  from './components/admin/FridgeManagement';
import AssignProducts    from './components/admin/AssignProducts';
import StockMonitoring   from './components/admin/StockMonitoring';
import SalesReport       from './components/admin/SalesReport';
import RefillSchedule    from './components/admin/RefillSchedule';
import AdminOrders       from './components/admin/AdminOrders';
import AdminCustomers    from './components/admin/AdminCustomers';
import StaffManager      from './components/admin/StaffManager';

// Customer Components
import Home              from './components/customer/Home';
import Products          from './components/customer/Products';
import Cart              from './components/customer/Cart';
import Checkout          from './components/customer/Checkout';
import OrderConfirmation from './components/customer/OrderConfirmation';

// QR Fridge Components
import QRScanner      from './components/qr-customer/QRScanner';
import FridgeProducts from './components/qr-customer/FridgeProducts';
import FridgeCheckout from './components/qr-customer/FridgeCheckout';
import PaymentSuccess from './components/qr-customer/PaymentSuccess';

// ─────────────────────────────────────────────────────────────────────────────
// ProtectedRoute
// ─────────────────────────────────────────────────────────────────────────────
const ProtectedRoute = ({ element, perm }) => {
  const { admin, loading, hasPermission } = useAuth();

  // 1. Wait for /admin/me to finish before deciding anything
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#f1f5f9',
        flexDirection: 'column', gap: '1rem',
        fontFamily: 'Nunito, sans-serif',
      }}>
        <div style={{
          width: '2.5rem', height: '2.5rem',
          border: '3px solid #f3f4f6', borderTopColor: '#059669',
          borderRadius: '50%', animation: 'spin 0.7s linear infinite',
        }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <p style={{ color: '#64748b', fontWeight: 600, fontSize: '0.9rem' }}>
          Loading…
        </p>
      </div>
    );
  }

  // 2. Not logged in → go to login
  if (!admin) return <Navigate to="/admin/login" replace />;

  // 3. Logged in but no permission
  if (perm && !hasPermission(perm)) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#f1f5f9', fontFamily: 'Nunito, sans-serif',
        gap: '0.75rem',
      }}>
        <div style={{ fontSize: '3rem' }}>🔒</div>
        <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#111827' }}>
          Access Restricted
        </div>
        <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>
          Your role (<strong>{admin.role}</strong>) does not have access to this page.
        </div>
        <button
          onClick={() => window.history.back()}
          style={{
            marginTop: '0.5rem', padding: '0.6rem 1.5rem',
            borderRadius: '9999px',
            background: 'linear-gradient(135deg,#10b981,#059669)',
            color: 'white', border: 'none', fontWeight: 800,
            cursor: 'pointer', fontSize: '0.9rem',
            fontFamily: 'Nunito, sans-serif',
          }}>
          ← Go Back
        </button>
      </div>
    );
  }

  // 4. All good
  return element;
};

// ─────────────────────────────────────────────────────────────────────────────
// App — AuthProvider is OUTSIDE Router so ProtectedRoute can use useAuth()
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  return (
    <AuthProvider>                     {/* ← MUST be outside Router */}
      <CustomerAuthProvider>
        <CartProvider>
          <Router>
            <Toaster position="top-right" />
            <Routes>

              {/* ── Admin Routes ── */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* All roles (dashboard, stock, refill, orders) */}
              <Route path="/admin/dashboard"
                element={<ProtectedRoute element={<Dashboard />}       perm="dashboard"       />} />
              <Route path="/admin/stock-monitoring"
                element={<ProtectedRoute element={<StockMonitoring />} perm="stock_monitor"   />} />
              <Route path="/admin/refill-schedule"
                element={<ProtectedRoute element={<RefillSchedule />}  perm="refill_schedule" />} />
              <Route path="/admin/orders"
                element={<ProtectedRoute element={<AdminOrders />}     perm="orders"          />} />

              {/* Admin only */}
              <Route path="/admin/products"
                element={<ProtectedRoute element={<ProductManagement />} perm="products"        />} />
              <Route path="/admin/fridges"
                element={<ProtectedRoute element={<FridgeManagement />}  perm="fridges"         />} />
              <Route path="/admin/assign-products"
                element={<ProtectedRoute element={<AssignProducts />}    perm="assign_products" />} />
              <Route path="/admin/sales-report"
                element={<ProtectedRoute element={<SalesReport />}       perm="sales_report"    />} />
              <Route path="/admin/customers"
                element={<ProtectedRoute element={<AdminCustomers />}    perm="customers"       />} />
              <Route path="/admin/staff"
                element={<ProtectedRoute element={<StaffManager />}      perm="customers"       />} />

              {/* ── Customer Routes ── */}
              <Route path="/"                                element={<Home />} />
              <Route path="/products"                        element={<Products />} />
              <Route path="/cart"                            element={<Cart />} />
              <Route path="/checkout"                        element={<Checkout />} />
              <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmation />} />

              {/* ── QR Fridge Routes ── */}
              <Route path="/scan"                   element={<QRScanner />} />
              <Route path="/fridge/:fridgeId"       element={<FridgeProducts />} />
              <Route path="/fridge-store/:fridgeId" element={<FridgeProducts />} />
              <Route path="/fridge-checkout"        element={<FridgeCheckout />} />
              <Route path="/payment-success"        element={<PaymentSuccess />} />

              {/* ── Fallback ── */}
              <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>
          </Router>
        </CartProvider>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default App;